import { useState, useRef } from "react"
import { alpha } from "@mui/material/styles"
import { Box, Button, Chip, CircularProgress, FormLabel, Tab, Tabs, Typography } from "@mui/material"
import { useToast } from "@crm-src/hooks/use-toast"
import {
  X,
  File,
  Image,
  FileText,
  Upload,
  FolderOpen,
  HardDrive
} from "lucide-react"
import campaignAttachmentsApi, { type CampaignAttachment } from "@crm-src/lib/campaign-attachments-api"
import FileManagerSelectorDialog, { type SelectedFile } from "@crm-src/components/file-manager-selector-dialog"

export interface CampaignAttachedFile {
  id: string
  name: string
  size: number
  type: string
  blob_key?: string
  url?: string
  file?: File // Store actual File object for local files
  isUploading?: boolean
  uploadError?: string
  source?: 'desktop' | 'file_manager' // Track where file came from
  fileManagerId?: string // ID from file manager if sourced from there
}

interface CampaignFileAttachmentProps {
  campaignId?: string // If provided, files will be uploaded to server
  files: CampaignAttachedFile[]
  onChange: (files: CampaignAttachedFile[]) => void
  disabled?: boolean
  maxFiles?: number
  maxSizePerFile?: number // in MB
}

// Map a campaign's SAVED attachments (from crm-api `campaign_json`, shape
// { id, name, size, type, blob_key, url }) into editor file rows so re-opening a
// campaign shows them. Server-side ids are numbers → coerce to string. These have
// no local File object (display-only) and aren't re-uploaded on save.
export function savedAttachmentsToFiles(
  attachments: ReadonlyArray<Record<string, any>> | null | undefined,
): CampaignAttachedFile[] {
  return (attachments || []).map((a) => ({
    id: String(a.id),
    name: a.name || 'attachment',
    size: typeof a.size === 'number' ? a.size : 0,
    type: a.type || '',
    ...(a.blob_key ? { blob_key: String(a.blob_key) } : {}),
    ...(a.url ? { url: String(a.url) } : {}),
  }))
}

// Helper function to upload local files and link file manager files to a campaign
export const uploadLocalFilesToCampaign = async (
  files: CampaignAttachedFile[],
  campaignId: string
): Promise<CampaignAttachedFile[]> => {
  // Separate local files from file manager files
  // String(file.id): a saved attachment's server id can be a NUMBER (the type
  // says string but JSON gives a number) — `.startsWith` would throw
  // ("s.id.startsWith is not a function", crashing Send Test on a campaign with
  // existing attachments).
  const localFiles = files.filter(file => String(file.id).startsWith('local-') && file.file)
  const fileManagerFiles = files.filter(file => file.source === 'file_manager' && file.fileManagerId)


  const results: CampaignAttachedFile[] = []

  // Upload local files
  const uploadPromises = localFiles.map(async (localFile) => {
    try {
      const uploadedFile = await campaignAttachmentsApi.uploadFile(campaignId, localFile.file!)

      return {
        id: uploadedFile.id,
        name: uploadedFile.name,
        size: uploadedFile.size,
        type: uploadedFile.type,
        blob_key: uploadedFile.blob_key,
        url: uploadedFile.url,
        source: 'desktop' as const
      }
    } catch (error) {
      console.error(`❌ Failed to upload ${localFile.name}:`, error)
      return {
        ...localFile,
        uploadError: error instanceof Error ? error.message : 'Upload failed'
      }
    }
  })

  if (uploadPromises.length > 0) {
    const uploadResults = await Promise.all(uploadPromises)
    results.push(...uploadResults)
  }

  // Link file manager files
  if (fileManagerFiles.length > 0) {
    const fileManagerIds = fileManagerFiles.map(file => file.fileManagerId!).filter(Boolean)
    if (fileManagerIds.length > 0) {
      try {
        const linkedFiles = await campaignAttachmentsApi.linkFileManagerFiles(campaignId, fileManagerIds)

        // Convert linked files to our format
        const linkedFileResults = linkedFiles.map(file => ({
          id: file.id,
          name: file.name,
          size: file.size,
          type: file.type,
          blob_key: file.blob_key,
          url: file.url,
          source: 'file_manager' as const
        }))

        results.push(...linkedFileResults)
      } catch (error) {
        console.error('❌ Failed to link file manager files:', error)
        // Add error info to file manager files
        const errorResults = fileManagerFiles.map(file => ({
          ...file,
          uploadError: error instanceof Error ? error.message : 'Failed to link file'
        }))
        results.push(...errorResults)
      }
    }
  }

  return results
}

export default function CampaignFileAttachment({
  campaignId,
  files,
  onChange,
  disabled = false,
  maxFiles = 3,
  maxSizePerFile = 10 // 10MB for email attachments
}: CampaignFileAttachmentProps) {

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isFileManagerDialogOpen, setIsFileManagerDialogOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<'desktop' | 'file-manager'>('desktop')
  const { toast } = useToast()

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
  }

  // Deliberately NOT the shared `<FileTypeIcon>` / `fileIconStyle`: those key off
  // a CRM `FileItem` (name + content_type, extension-aware). Here the only input
  // is a bare MIME string from a transient browser `File` upload, and the glyph
  // set is intentionally different (image/fallback rendered uncoloured). Separate
  // input contract → kept as its own helper.
  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) {
      return <Image size={16} />
    }
    if (type === 'application/pdf') {
      return <Box sx={{ display: "flex", color: "error.main" }}><FileText size={16} /></Box>
    }
    if (type.includes('word') || type.includes('document')) {
      return <Box sx={{ display: "flex", color: "primary.main" }}><FileText size={16} /></Box>
    }
    if (type.includes('sheet') || type.includes('excel')) {
      return <Box sx={{ display: "flex", color: "success.main" }}><FileText size={16} /></Box>
    }
    return <File size={16} />
  }

  const uploadFileToServer = async (file: File): Promise<CampaignAttachedFile> => {
    if (!campaignId) {
      // If no campaign ID, return local file with File object stored
      return {
        id: `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: file.name,
        size: file.size,
        type: file.type,
        file: file, // Store the actual File object
        url: URL.createObjectURL(file),
        source: 'desktop'
      }
    }

    try {
      const uploadedFile = await campaignAttachmentsApi.uploadFile(campaignId, file)

      return {
        id: uploadedFile.id,
        name: uploadedFile.name,
        size: uploadedFile.size,
        type: uploadedFile.type,
        blob_key: uploadedFile.blob_key,
        url: uploadedFile.url,
        source: 'desktop'
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to upload file')
    }
  }

  const handleFileManagerSelection = (selectedFiles: SelectedFile[]) => {

    const newFiles: CampaignAttachedFile[] = selectedFiles.map(file => ({
      id: `fm-${file.id}`,
      name: file.name,
      size: file.size,
      type: file.type,
      url: file.downloadUrl,
      source: 'file_manager',
      fileManagerId: file.id
    }))


    // Check for duplicates
    const existingFileManagerIds = files
      .filter(f => f.source === 'file_manager')
      .map(f => f.fileManagerId)

    const uniqueNewFiles = newFiles.filter(newFile =>
      !existingFileManagerIds.includes(newFile.fileManagerId)
    )


    if (uniqueNewFiles.length !== newFiles.length) {
      toast({
        title: "Some files already attached",
        description: "Duplicate files from file manager were skipped",
        variant: "default",
      })
    }

    if (uniqueNewFiles.length > 0) {
      const updatedFiles = [...files, ...uniqueNewFiles]
      onChange(updatedFiles)
      toast({
        title: "Files attached",
        description: `${uniqueNewFiles.length} file${uniqueNewFiles.length === 1 ? '' : 's'} attached from file manager`,
      })
    }
  }

  const handleFiles = async (selectedFiles: FileList) => {
    const newFiles: CampaignAttachedFile[] = []
    const maxSizeBytes = maxSizePerFile * 1024 * 1024

    // Check if we've reached max files
    if (files.length >= maxFiles) {
      toast({
        title: "Too many files",
        description: `Maximum ${maxFiles} files allowed`,
        variant: "destructive",
      })
      return
    }

    for (const file of Array.from(selectedFiles)) {
      // Check if we've reached max files with new additions
      if (files.length + newFiles.length >= maxFiles) {
        break
      }

      // Validate file
      const validation = campaignAttachmentsApi.validateFile(file)
      if (!validation.valid) {
        toast({
          title: "Invalid file",
          description: validation.error,
          variant: "destructive",
        })
        continue
      }

      // Allow duplicate file names - backend will auto-rename if needed
      // Create temporary file entry with upload state
      const tempFile: CampaignAttachedFile = {
        id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: file.name,
        size: file.size,
        type: file.type,
        isUploading: true
      }

      newFiles.push(tempFile)
    }

    if (newFiles.length === 0) return

    // Add files immediately to show upload state
    const updatedFiles = [...files, ...newFiles]
    onChange(updatedFiles)

    // Upload files one by one
    const uploadPromises = newFiles.map(async (tempFile, index) => {
      const file = Array.from(selectedFiles)[index]

      try {
        const uploadedFile = await uploadFileToServer(file)

        // Update the temporary file with uploaded data
        const fileIndex = updatedFiles.findIndex(f => f.id === tempFile.id)
        if (fileIndex !== -1) {
          updatedFiles[fileIndex] = {
            ...uploadedFile,
            isUploading: false
          }
          onChange([...updatedFiles])
        }

        if (campaignId) {
          toast({
            title: "Success",
            description: `File "${uploadedFile.name}" attached successfully`,
          })
        }
      } catch (error: any) {
        // Update file with error state
        const fileIndex = updatedFiles.findIndex(f => f.id === tempFile.id)
        if (fileIndex !== -1) {
          updatedFiles[fileIndex] = {
            ...tempFile,
            isUploading: false,
            uploadError: error.message
          }
          onChange([...updatedFiles])
        }

        toast({
          title: "Upload Failed",
          description: error.message,
          variant: "destructive",
        })
      }
    })

    await Promise.all(uploadPromises)
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files
    if (selectedFiles) {
      handleFiles(selectedFiles)
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const removeFile = async (fileId: string) => {
    const file = files.find(f => f.id === fileId)
    if (!file) return

    try {
      // If file was uploaded to server, delete it
      if (campaignId && file.blob_key && !String(file.id).startsWith('temp-') && !String(file.id).startsWith('local-')) {
        await campaignAttachmentsApi.deleteFile(campaignId, file.id)
      }

      // If file has a local URL (object URL), revoke it
      if (file.url && file.url.startsWith('blob:')) {
        URL.revokeObjectURL(file.url)
      }

      // Remove from state
      onChange(files.filter(f => f.id !== fileId))

      if (campaignId) {
        toast({
          title: "Success",
          description: `File "${file.name}" removed successfully`,
        })
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.error || 'Failed to remove file',
        variant: "destructive",
      })
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)

    if (disabled) return

    const droppedFiles = e.dataTransfer.files
    if (droppedFiles) {
      handleFiles(droppedFiles)
    }
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <FormLabel>Email Attachments</FormLabel>

      <Box sx={{ width: "100%" }}>
        <Tabs
          value={activeTab}
          onChange={(_e, v) => setActiveTab(v)}
          variant="fullWidth"
        >
          <Tab
            value="desktop"
            iconPosition="start"
            icon={<HardDrive size={16} />}
            label="Upload from Desktop"
          />
          <Tab
            value="file-manager"
            iconPosition="start"
            icon={<FolderOpen size={16} />}
            label="Select from File Manager"
          />
        </Tabs>

        {activeTab === 'desktop' && (
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
            {/* Upload Area */}
            <Box
              sx={{
                border: 2,
                borderStyle: "dashed",
                borderRadius: 1,
                p: 2,
                textAlign: "center",
                transition: "border-color 0.15s, background-color 0.15s",
                borderColor: isDragging ? "primary.main" : "divider",
                bgcolor: isDragging ? (t) => alpha(t.palette.primary.main, 0.05) : "transparent",
                opacity: disabled ? 0.5 : 1,
                cursor: disabled ? "not-allowed" : "pointer",
                ...(disabled
                  ? {}
                  : { "&:hover": { borderColor: "primary.main", bgcolor: (t) => alpha(t.palette.primary.main, 0.05) } }),
              }}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => !disabled && fileInputRef.current?.click()}
            >
              <Box sx={{ display: "flex", justifyContent: "center", color: "text.disabled", mb: 0.5 }}><Upload size={24} /></Box>
              <Typography sx={{ fontSize: 14, color: "text.secondary", mb: 0.5 }}>
                Drop files here or click to select
              </Typography>
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                Max {maxFiles} files, {maxSizePerFile}MB each
              </Typography>

              <Box
                component="input"
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                sx={{ display: "none" }}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp,.zip"
                disabled={disabled}
              />
            </Box>
          </Box>
        )}

        {activeTab === 'file-manager' && (
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
            {/* File Manager Selection */}
            <Box sx={{ border: 2, borderStyle: "dashed", borderColor: "divider", borderRadius: 1, p: 2, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
              <Box sx={{ display: "flex", color: "text.disabled", mb: 1 }}><FolderOpen size={24} /></Box>
              <Typography sx={{ fontSize: 14, color: "text.secondary", mb: 1.5 }}>
                Select files from your file manager
              </Typography>
              <Button
                type="button"
                onClick={() => setIsFileManagerDialogOpen(true)}
                disabled={disabled || files.length >= maxFiles}
                variant="outlined"
                color="inherit"
                startIcon={<FolderOpen size={16} />}
              >
                Browse File Manager
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* File List */}
      {files.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <FormLabel sx={{ fontSize: 14 }}>Attached Files ({files.length}/{maxFiles})</FormLabel>
          {files.map((file) => (
            <Box key={file.id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 1, bgcolor: "surfaceSoft", borderRadius: 1, border: 1, borderColor: "divider" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: 1 }}>
                {file.isUploading ? (
                  <CircularProgress size={16} sx={{ color: "primary.main" }} />
                ) : file.uploadError ? (
                  <Box sx={{ display: "flex", color: "error.main" }}><X size={16} /></Box>
                ) : (
                  getFileIcon(file.type)
                )}

                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</Typography>
                    {file.source === 'file_manager' && (
                      <Chip size="small" variant="outlined" label="File Manager" sx={{ fontSize: 12 }} />
                    )}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography component="span" sx={{ fontSize: 12, color: "text.secondary" }}>{formatFileSize(file.size)}</Typography>
                    {file.isUploading && (
                      <Typography component="span" sx={{ fontSize: 12, color: "primary.main" }}>Uploading...</Typography>
                    )}
                    {file.uploadError && (
                      <Typography component="span" sx={{ fontSize: 12, color: "error.main" }}>Upload failed</Typography>
                    )}
                  </Box>
                </Box>
              </Box>

              <Button
                type="button"
                variant="text"
                color="error"
                size="small"
                onClick={() => removeFile(file.id)}
                disabled={!!(disabled || file.isUploading)}
                sx={{ minWidth: 32, width: 32, height: 32, p: 0 }}
              >
                <X size={16} />
              </Button>
            </Box>
          ))}
        </Box>
      )}

      {/* File Manager Selection Dialog */}
      <FileManagerSelectorDialog
        isOpen={isFileManagerDialogOpen}
        onClose={() => setIsFileManagerDialogOpen(false)}
        onSelect={handleFileManagerSelection}
        maxFiles={maxFiles - files.length}
        selectedFileIds={files.filter(f => f.source === 'file_manager').map(f => f.fileManagerId || '')}
      />
    </Box>
  )
}
